/**
 * The dashboard themes a person can pick in Settings.
 *
 * What each theme *does* to colours lives in styles/palette.css, generated by
 * scripts/make-palette.cjs from the same ids; this file is what the picker
 * shows — a name, a line, and swatches that preview the theme before it is
 * applied. themes.test.mjs checks both lists name the same themes.
 *
 * Eight themes: four dark and four light. The default stays the dark theme the
 * product has always shipped in, so nobody's screen changes until they choose.
 */

export const DEFAULT_THEME = 'dark';

export const THEMES = Object.freeze([
  {
    id: 'dark',
    name: 'الليل الأزرق',
    hint: 'الداكن الأصلي — هادئ ومريح للعين في الورديات الطويلة',
    mode: 'dark',
    meta: '#020617',
    swatch: { bg: '#020617', panel: '#0f172a', accent: '#22d3ee', accent2: '#a78bfa', text: '#f8fafc', muted: '#94a3b8' },
  },
  {
    id: 'ocean',
    name: 'المحيط العميق',
    hint: 'أزرق بحري داكن بلمسات سماوية',
    mode: 'dark',
    meta: '#01101f',
    swatch: { bg: '#01101f', panel: '#071b33', accent: '#38bdf8', accent2: '#818cf8', text: '#f0f9ff', muted: '#7dd3fc' },
  },
  {
    id: 'emerald',
    name: 'الزمرد الهادئ',
    hint: 'أخضر داكن مريح بإضاءة زمردية',
    mode: 'dark',
    meta: '#02130f',
    swatch: { bg: '#02130f', panel: '#062019', accent: '#34d399', accent2: '#2dd4bf', text: '#ecfdf5', muted: '#6ee7b7' },
  },
  {
    id: 'violet',
    name: 'البنفسج الملكي',
    hint: 'بنفسجي عميق بتوهج نيون',
    mode: 'dark',
    meta: '#0a0418',
    swatch: { bg: '#0a0418', panel: '#150c2b', accent: '#a78bfa', accent2: '#e879f9', text: '#f5f3ff', muted: '#c4b5fd' },
  },
  {
    id: 'light',
    name: 'النهار الصافي',
    hint: 'فاتح ونظيف — مناسب للمكاتب المضيئة',
    mode: 'light',
    meta: '#eef4fa',
    swatch: { bg: '#eef4fa', panel: '#ffffff', accent: '#0891b2', accent2: '#7c3aed', text: '#0f172a', muted: '#475569' },
  },
  {
    id: 'sand',
    name: 'الرمال الدافئة',
    hint: 'فاتح بدرجات دافئة وفيروزية',
    mode: 'light',
    meta: '#f7f2ea',
    swatch: { bg: '#f7f2ea', panel: '#fffdf9', accent: '#0d9488', accent2: '#d97706', text: '#1c1917', muted: '#57534e' },
  },
  {
    id: 'mint',
    name: 'النعناع الهادئ',
    hint: 'فاتح بخضرة ناعمة — مريح للعين في العيادات المضيئة',
    mode: 'light',
    meta: '#eef7f3',
    swatch: { bg: '#eef7f3', panel: '#fbfefc', accent: '#059669', accent2: '#0284c7', text: '#0f2a22', muted: '#4a6359' },
  },
  {
    id: 'lavender',
    name: 'الخزامى الناعمة',
    hint: 'فاتح بدرجات بنفسجية هادئة ولمسة وردية',
    mode: 'light',
    meta: '#f4f2fb',
    swatch: { bg: '#f4f2fb', panel: '#fdfcff', accent: '#7c3aed', accent2: '#db2777', text: '#1e1838', muted: '#5b5474' },
  },
]);

export const THEME_IDS = Object.freeze(THEMES.map((t) => t.id));

export const isTheme = (id) => THEME_IDS.includes(id);

export const themeById = (id) => THEMES.find((t) => t.id === id) || THEMES[0];

/** Is this theme dark? (المرحلة 1.35: an institution's dark mark goes on a dark one.) */
export const isDarkTheme = (id) => themeById(id).mode === 'dark';

/**
 * Is the page dark right now — for a screen outside the ThemeProvider (the
 * public pages), which follows the theme applyTheme() put on <html>.
 */
export function isDarkPage() {
  if (typeof document === 'undefined') return isDarkTheme(DEFAULT_THEME);
  const mode = document.documentElement.getAttribute('data-theme-mode');
  return mode ? mode === 'dark' : isDarkTheme(DEFAULT_THEME);
}

/** Where the chosen theme is remembered on this device — read before React boots. */
export const THEME_STORAGE_KEY = 'capsule_theme';

/**
 * Put a theme on the document: the attribute palette.css keys off, the colour
 * scheme the browser's own controls follow, and the phone's status-bar tint.
 */
export function applyTheme(id, { animate = false } = {}) {
  if (typeof document === 'undefined') return;
  const theme = themeById(id);
  const root = document.documentElement;
  if (animate) {
    root.classList.add('theme-switching');
    window.setTimeout(() => root.classList.remove('theme-switching'), 500);
  }
  root.setAttribute('data-theme', theme.id);
  root.setAttribute('data-theme-mode', theme.mode);
  root.style.colorScheme = theme.mode;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme.meta);
}
